import { FC, Fragment, useMemo, useState } from "react"
import { useQuery } from "react-query"

import { Button, Checkbox, Switch } from "@synapse-analytics/synapse-ui"

import { VisionAPI } from "../../../../API/VisionAPI"
import Placeholder from "../../../../components/Placeholder"
import { useBranchesStore } from "../../../../store"
import { definitions } from "../../../../types/Generated/apiTypes"

import styles from "../BranchesAndCollectionsMenu.module.scss"

type Branch = definitions["Branch"]

/**
 * Props for the BranchesTab component.
 */
interface BranchesTabProps {
  /**
   * Callback called when the user chooses to add a new collection.
   */
  onAddNewCollection: () => void
}

/**
 * BranchesTab component displays all branches with filtering and selection capabilities.
 *
 * Features:
 * - Displays all branches as checkboxes for selection
 * - Toggle switch to filter and show only uncollected branches
 * - Branch selection state is managed globally via useBranchesStore
 * - Provides "Add New Collection" button
 * - Shows loading and empty states
 *
 * @param props - The component props
 * @returns The rendered branches tab component
 */
const BranchesTab: FC<BranchesTabProps> = ({ onAddNewCollection }) => {
  const [showUncollectedOnly, setShowUncollectedOnly] = useState(false)
  const [selectedBranches, setSelectedBranches] = useBranchesStore((state) => [
    state.selectedBranches ?? [],
    state.setSelectedBranches,
  ])

  /**
   * Memoized Set of selected branch IDs for O(1) membership checks.
   * Used to efficiently determine if a branch is selected.
   */
  const selectedBranchesSet = useMemo(() => new Set(selectedBranches), [selectedBranches])

  /**
   * Fetches branches with optional filtering by collection status.
   * The query key includes the filter state to enable/disable the filter dynamically.
   */
  const { data: branches, isLoading: areBranchesLoading } = useQuery<Branch[]>(
    ["fetchBranches", !showUncollectedOnly ? null : false],
    ({ queryKey }) => VisionAPI.fetchBranches({ has_collection: queryKey[1] as boolean })
  )

  return (
    <Fragment>
      <div className={styles.switchAndBranches}>
        <Switch
          label="Show Uncollected Only"
          checked={showUncollectedOnly}
          onChange={(e) => setShowUncollectedOnly(e.target.checked)}
        />
        <div className={styles.checkboxContainer}>
          {areBranchesLoading ? (
            <div className={styles.emptyListContainer}>
              <Placeholder svgHeight="75px" svgWidth="62px" hasBackground={false} isLoading />
            </div>
          ) : (
            <Fragment>
              {branches?.length === 0 ? (
                <div className={styles.emptyListContainer}>
                  <Placeholder
                    svgHeight="75px"
                    svgWidth="62px"
                    hasBackground={false}
                    description="No branches available"
                    descriptionVariant="a"
                    subDescription={showUncollectedOnly ? "No uncollected branches found." : ""}
                    subDescriptionVariant="label-regular"
                  />
                </div>
              ) : (
                branches?.map((branch) => (
                  <div key={branch.id}>
                    <Checkbox
                      label={branch.name}
                      checked={selectedBranchesSet.has(branch.id!)}
                      name={branch.name}
                      onChange={(e) => {
                        const isChecked = (e.target as HTMLInputElement).checked
                        if (isChecked) {
                          setSelectedBranches([...selectedBranches, branch.id!])
                        } else {
                          setSelectedBranches(selectedBranches.filter((id) => id !== branch.id!))
                        }
                      }}
                    />
                  </div>
                ))
              )}
            </Fragment>
          )}
        </div>
      </div>
      <Button
        color="primary"
        variant="primary"
        size="small"
        fullWidth
        onClick={onAddNewCollection}
        className={styles.actionButton}
      >
        Add New Collection
      </Button>
    </Fragment>
  )
}

export default BranchesTab
